import { useParams } from "react-router-dom";
import PageLayout from "@/components/layout/HomePageLayout";
import ProfileDetailContent from "@/components/profile/ProfileDetailContent";
import { useProfile } from "@/v2/hooks/profiles/useProfiles";
import { useProfileTracking } from "@/v2/hooks/profiles/useProfileTracking";
import { useAuth } from "@/hooks/auth";
import { useEffect, useState } from "react";
import SEOHead from "@/components/seo/SEOHead";
import { useQueryClient } from "@tanstack/react-query";
import HireMeScrollPopup from "@/components/profile/HireMeScrollPopup";
import HireMeSuccessModal from "@/components/profile/HireMeSuccessModal";
import HireMeModal from "@/components/profile/HireMeModal";
import { useIsMobile } from "@/hooks/use-mobile";
import { generateProfileStructuredData } from "@/utils/structuredDataUtils";
import { getSocialMediaSafeImageUrl } from "@/utils/imageUtils";

interface ProfileDetailProps {
  profileOverride?: any;
  openPortfolioModal?: any;
  onClosePortfolioModal?: () => void;
}

const ProfileDetail: React.FC<ProfileDetailProps> = ({
  profileOverride,
  openPortfolioModal,
  onClosePortfolioModal,
}) => {
  const { id, username } = useParams<{ id?: string; username?: string }>();
  const idOrSlug = username || id;
  const placeholderImage = "/lovable-uploads/default-profile-avatar.png";
  const { data: profileData, isLoading, error } = useProfile(idOrSlug);
  // Use override if provided
  const profileDataToUse = profileOverride ? { data: profileOverride } : profileData;
  const { trackProfileView } = useProfileTracking();
  // const [hasTrackedView, setHasTrackedView] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  // State for Hire Me modal (for scroll popup)
  const [isHireMeScrollPopupVisible, setIsHireMeScrollPopupVisible] =
    useState(false);
  // State for Hire Me Success modal
  const [isHireMeSuccessModalOpen, setIsHireMeSuccessModalOpen] =
    useState(false);
  // State for primary Hire Me modal
  const [isPrimaryHireMeModalOpen, setIsPrimaryHireMeModalOpen] =
    useState(false);

  // Function to open the Hire Me modal (for scroll popup)
  const handleOpenHireMeModal = () => {
    // Only open if not on mobile and hireMeEnabled is true
    if (!isMobile && enhancedProfile?.hireMeEnabled) {
      setIsHireMeScrollPopupVisible(true);
    }
  };

  // Function to close the Hire Me modal (scroll popup)
  const handleCloseHireMeModal = () => {
    setIsHireMeScrollPopupVisible(false);
  };

  // Function to open the primary Hire Me modal
  const handleOpenPrimaryHireMeModal = () => {
    if (enhancedProfile?.hireMeEnabled) {
      setIsPrimaryHireMeModalOpen(true);
    }
  };

  // Function to close the primary Hire Me modal
  const handleClosePrimaryHireMeModal = () => {
    setIsPrimaryHireMeModalOpen(false);
  };

  // Function to handle successful Hire Me submission
  const handleHireMeSubmitSuccess = () => {
    setIsHireMeScrollPopupVisible(false); // Close the Hire Me scroll modal if open
    setIsPrimaryHireMeModalOpen(false); // Close the primary Hire Me modal
    setIsHireMeSuccessModalOpen(true); // Open the success modal
  };

  // Function to close the Hire Me Success modal
  const handleCloseHireMeSuccessModal = (open: boolean) => {
    setIsHireMeSuccessModalOpen(open);
  };
  // Tracking profile views
  useEffect(() => {
    if (profileDataToUse && profileDataToUse.data && profileDataToUse.data.id) {
      console.log("Tracking profile view for:", profileDataToUse.data.id);
      trackProfileView(profileDataToUse.data);
    }
  }, [profileDataToUse]);

  // Set up a listener for profile data changes
  // useEffect(() => {
  //   if (profileData && profileData.data?.id) {
  //     // Set up a polling interval for the profile data to catch like updates
  //     const intervalId = setInterval(() => {
  //       console.log(
  //         `[ProfileDetail] Refreshing profile data for: ${profileData.data.id}`
  //       );
  //       queryClient.invalidateQueries({ queryKey: ["profile", profileData.data.id] });
  //     }, 30000); // Poll every 30 seconds

  //     return () => clearInterval(intervalId);
  //   }
  // }, [profileData, queryClient]);

  // Get name for the title
  const profileName =
    profileDataToUse?.data?.display_name ||
    profileDataToUse?.data?.username ||
    "Designer";
  const profileTitle = profileDataToUse?.data?.profession || "Designer";
  const profileDescription = profileDataToUse?.data?.bio
    ? `${profileDataToUse.data.bio.substring(0, 150)}${
        profileDataToUse.data.bio.length > 150 ? "..." : ""
      }`
    : `${profileName} is a ${profileTitle} on AfricansWhoDesign.`;

  // Create a more specific loading title using route parameters
  const getLoadingTitle = () => {
    if (username) {
      return `${username} - Designer Profile | AfricansWhoDesign`;
    }
    if (id) {
      return `Designer Profile | AfricansWhoDesign`;
    }
    return "Loading Profile | AfricansWhoDesign";
  };

  // Create an enhanced profile object with hireMeEnabled in camelCase
  const enhancedProfile = profileDataToUse?.data
    ? {
        ...profileDataToUse.data,
        hireMeEnabled: (profileDataToUse.data as { hire_me_enabled?: boolean })
          .hire_me_enabled, // Properly typed
      }
    : null;

  // Process image URL once with proper fallback logic
  const socialMediaImageUrl = enhancedProfile?.avatar_url
    ? getSocialMediaSafeImageUrl(enhancedProfile.avatar_url) || placeholderImage
    : placeholderImage;

  // Debug logging for social media image URL
  console.log("[ProfileDetail] Social media image URL:", {
    original: enhancedProfile?.avatar_url,
    processed: socialMediaImageUrl,
    isPlaceholder: socialMediaImageUrl === placeholderImage,
  });

  // Debug logging for title changes
  console.log("[ProfileDetail] Title info:", {
    isLoading,
    hasProfileData: !!profileDataToUse?.data,
    profileName,
    profileTitle,
    username,
    id,
    finalTitle:
      isLoading || !enhancedProfile
        ? getLoadingTitle()
        : `${profileName} - ${profileTitle} | AfricansWhoDesign`,
  });

  return (
    <PageLayout>
      {/* Always render SEOHead, even during loading */}
      <SEOHead
        title={
          isLoading || !enhancedProfile
            ? getLoadingTitle()
            : `${profileName} - ${profileTitle} | AfricansWhoDesign`
        }
        description={
          isLoading || !enhancedProfile
            ? "Loading designer profile..."
            : profileDescription
        }
        imageUrl={socialMediaImageUrl}
        type="profile"
        structuredData={
          enhancedProfile
            ? generateProfileStructuredData(enhancedProfile)
            : undefined
        }
      />

      {/* Render loading state or profile content */}
      {isLoading || !enhancedProfile ? (
        <div className="flex justify-center items-center h-screen">
          Loading...
        </div>
      ) : (
        <>
          <div className="relative z-30">
            <ProfileDetailContent
              profile={enhancedProfile}
              loading={isLoading}
              error={error}
              onRetry={() =>
                queryClient.invalidateQueries({
                  queryKey: ["profile", idOrSlug],
                })
              }
              isSimplifiedView={false}
              placeholderImage={placeholderImage}
              onOpenHireMeModal={handleOpenHireMeModal}
              onOpenPrimaryHireMeModal={handleOpenPrimaryHireMeModal}
              onSetHireMeScrollPopupVisible={setIsHireMeScrollPopupVisible}
              openPortfolioModal={openPortfolioModal}
              onClosePortfolioModal={onClosePortfolioModal}
            />
          </div>

          {enhancedProfile?.hireMeEnabled && (
            <HireMeScrollPopup
              isOpen={isHireMeScrollPopupVisible}
              onClose={handleCloseHireMeModal}
              email={enhancedProfile?.email || ""}
              onHireMeClick={handleOpenPrimaryHireMeModal}
            />
          )}

          <HireMeSuccessModal
            isOpen={isHireMeSuccessModalOpen}
            onOpenChange={handleCloseHireMeSuccessModal}
            designerName={
              enhancedProfile?.display_name ||
              enhancedProfile?.username ||
              "Designer"
            }
          />

          {/* Render the primary Hire Me Modal */}
          <HireMeModal
            profile={enhancedProfile}
            isOpen={isPrimaryHireMeModalOpen}
            onOpenChange={handleClosePrimaryHireMeModal}
            designerName={
              enhancedProfile?.display_name ||
              enhancedProfile?.username ||
              "Designer"
            }
            designerId={enhancedProfile?.id}
            designerUsername={enhancedProfile?.username}
            onSubmitSuccess={handleHireMeSubmitSuccess}
          />
        </>
      )}
    </PageLayout>
  );
};

export default ProfileDetail;
